import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import LinkIcon from "@mui/icons-material/Link";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { formatSourceUrl } from "../../../utilities/formatSourceUrl";

import {
  API_post_discover_site_pages,
  API_post_file_upload_from_url,
} from "../../../constants";
import { postRightworkAPI } from "../requests/utilities/requests";

// Hard cap on pages per capture. Imports run sequentially (each one
// fetches + extracts + chunk-indexes inline, ~2-4s), so the cap bounds
// the manager's wait to ~a minute; a site with more worth importing
// can run a second capture.
const MAX_IMPORT_PAGES = 15;

/**
 * Add-from-link as SITE capture: fetch the entered page, discover the
 * same-site pages it links to (backend post_discover_site_pages), and
 * let the manager pick which to import. Each selected page imports
 * sequentially through the existing single-page endpoint — per-page
 * failures don't abort the rest, and a Stop button halts after the
 * in-flight page.
 *
 * Steps: url → pick → importing → done. Selection defaults to just the
 * entered page, so "paste URL, Enter, Import" stays as fast as the old
 * single-page modal.
 */
export default function SiteCaptureModal({
  open,
  onClose,
  companyId,
  currentFolder,
  folderName,
  // undefined = unknown (don't warn); false = warn + offer enable.
  folderSearchable,
  // (folderUuid, true) — parent's optimistic toggle. Null when the user
  // lacks the documents-editor permission; the warning then renders
  // without the enable action.
  onToggleSearchable,
  // Called once after an import run that stored at least one page,
  // with the imported [{uuid, url, name}] so callers can auto-select.
  onImported,
  // Picker mode: imports land in the auto-managed assistant-searchable
  // "Imported pages" folder instead of currentFolder (backend
  // use_ai_sources_folder). The searchability warning is moot there.
  useAiSourcesFolder = false,
}) {
  const [step, setStep] = React.useState("url");
  const [url, setUrl] = React.useState("");
  const [discovering, setDiscovering] = React.useState(false);
  const [discoverError, setDiscoverError] = React.useState(null);
  const [page, setPage] = React.useState(null); // {url, title}
  const [links, setLinks] = React.useState([]); // [{url, label}]
  const [selected, setSelected] = React.useState(new Set());
  const [progress, setProgress] = React.useState(null); // {done,total,label}
  const [results, setResults] = React.useState(null); // {imported:[], failed:[]}
  const stopRef = React.useRef(false);

  // Unmount mid-import (e.g. navigating away from the Knowledge Hub):
  // stop issuing requests after the in-flight page. Pages already
  // imported are stored server-side either way.
  React.useEffect(() => {
    return () => {
      stopRef.current = true;
    };
  }, []);

  const importing = step === "importing";

  React.useEffect(() => {
    if (open) {
      setStep("url");
      setUrl("");
      setDiscoverError(null);
      setPage(null);
      setLinks([]);
      setSelected(new Set());
      setProgress(null);
      setResults(null);
      stopRef.current = false;
    }
  }, [open]);

  const handleDiscover = async () => {
    const trimmed = url.trim();
    if (!trimmed || discovering) return;
    setDiscovering(true);
    setDiscoverError(null);
    try {
      const res = await postRightworkAPI(API_post_discover_site_pages, {
        company_id: companyId,
        url: trimmed,
      });
      const payload = res?.data?.redux?.payload;
      const discoveredPage = payload?.page;
      if (!discoveredPage?.url) throw new Error("No page returned");
      setPage(discoveredPage);
      setLinks(payload?.links || []);
      // Default selection: just the entered page. The checklist makes
      // adding more one click each; pre-selecting a whole nav would
      // make the common single-page case slower, not faster.
      setSelected(new Set([discoveredPage.url]));
      setStep("pick");
    } catch (err) {
      setDiscoverError(
        err?.response?.data?.system?.message ||
          "Couldn't read that page. Please check the URL and try again.",
      );
    } finally {
      setDiscovering(false);
    }
  };

  const toggleSelected = (pageUrl) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pageUrl)) {
        next.delete(pageUrl);
      } else if (next.size < MAX_IMPORT_PAGES) {
        next.add(pageUrl);
      }
      return next;
    });
  };

  const allRows = React.useMemo(() => {
    if (!page) return [];
    return [
      {
        url: page.url,
        label: page.title ? `This page — ${page.title}` : "This page",
      },
      ...links,
    ];
  }, [page, links]);

  const handleImport = async () => {
    const targets = allRows.filter((r) => selected.has(r.url));
    if (targets.length === 0 || importing) return;
    setStep("importing");
    stopRef.current = false;
    const imported = [];
    const failed = [];
    for (let i = 0; i < targets.length; i += 1) {
      if (stopRef.current) break;
      const target = targets[i];
      setProgress({ done: i, total: targets.length, label: target.label });
      try {
        const res = await postRightworkAPI(API_post_file_upload_from_url, {
          company_id: companyId,
          url: target.url,
          ...(useAiSourcesFolder
            ? { use_ai_sources_folder: true }
            : { file_upload_folder_uuid: currentFolder }),
        });
        const fu = res?.data?.redux?.payload?.file_upload;
        imported.push({
          uuid: fu?.uuid,
          url: target.url,
          name: fu?.name || target.label,
        });
      } catch (err) {
        failed.push({
          url: target.url,
          label: target.label,
          message:
            err?.response?.data?.system?.message ||
            "Couldn't import this page.",
        });
      }
    }
    setProgress(null);
    setResults({ imported, failed, stopped: stopRef.current });
    setStep("done");
    if (imported.length > 0) {
      onImported?.(imported);
      toast.success(
        `Imported ${imported.length} page${imported.length === 1 ? "" : "s"}`,
        { autoClose: 3000 },
      );
    }
  };

  const handleClose = () => {
    if (importing) return; // Stop first — imports are mid-flight.
    onClose?.();
  };

  const showSearchWarning = folderSearchable === false;

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="site-capture-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(560px, 94vw)",
          maxHeight: "min(82vh, 700px)",
          bgcolor: "background.paper",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2
            id="site-capture-title"
            className="text-base font-semibold text-slate-900"
          >
            Add from link
          </h2>
          <button
            onClick={handleClose}
            disabled={importing}
            className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:text-slate-300"
            aria-label="Close"
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Searchability warning — imports into a non-searchable
              folder never index and never extract photos (the Celery
              gates skip them), so they'd be invisible to AI generation.
              Surfaced on every step so it can't be missed. */}
          {showSearchWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <div className="font-semibold mb-0.5">
                This folder isn't searchable by the AI assistant
              </div>
              <p className="text-xs text-amber-800">
                Pages imported {folderName ? `into "${folderName}"` : "here"}{" "}
                won't be usable as AI generation sources, and their photos
                won't be extracted.
              </p>
              {onToggleSearchable && (
                <button
                  onClick={() => onToggleSearchable(currentFolder, true)}
                  className="mt-2 px-2.5 py-1 text-xs font-medium text-amber-900 bg-white border border-amber-300 rounded hover:bg-amber-100"
                >
                  Enable assistant search for this folder
                </button>
              )}
            </div>
          )}

          {step === "url" && (
            <>
              <p className="text-sm text-slate-500">
                Import a web page — or several pages from the same site —
                as documents in this folder.
              </p>
              <div>
                <label
                  htmlFor="siteCaptureUrl"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Page URL
                </label>
                <input
                  type="url"
                  id="siteCaptureUrl"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleDiscover();
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
                  placeholder="https://example.com"
                  autoFocus
                  disabled={discovering}
                />
              </div>
              {discoverError && (
                <p className="text-sm text-rose-600">{discoverError}</p>
              )}
            </>
          )}

          {step === "pick" && (
            <>
              <p className="text-sm text-slate-500">
                Choose which pages to import. Up to {MAX_IMPORT_PAGES} per
                run.
              </p>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
                {allRows.map((row) => {
                  const checked = selected.has(row.url);
                  const capped =
                    !checked && selected.size >= MAX_IMPORT_PAGES;
                  let path = "";
                  try {
                    path = new URL(row.url).pathname;
                  } catch (e) {
                    path = row.url;
                  }
                  return (
                    <button
                      key={row.url}
                      onClick={() => toggleSelected(row.url)}
                      disabled={capped}
                      className={`w-full px-4 py-2.5 flex items-center gap-3 text-left ${
                        checked ? "bg-violet-50" : "hover:bg-slate-50"
                      } ${capped ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span
                        className={`flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center ${
                          checked
                            ? "bg-violet-700 border-violet-700"
                            : "bg-white border-slate-300"
                        }`}
                      >
                        {checked && (
                          <CheckIcon style={{ fontSize: 14, color: "white" }} />
                        )}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">
                          {row.label || path}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {path}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {selected.size >= MAX_IMPORT_PAGES && (
                <p className="text-xs text-slate-500">
                  Page limit reached — import these, then run another
                  capture for the rest.
                </p>
              )}
            </>
          )}

          {step === "importing" && progress && (
            <div className="py-6 text-center space-y-3">
              <CircularProgress size={28} sx={{ color: "#6d28d9" }} />
              <div className="text-sm font-medium text-slate-800">
                Importing page {progress.done + 1} of {progress.total}…
              </div>
              <div className="text-xs text-slate-500 truncate px-6">
                {progress.label}
              </div>
            </div>
          )}

          {step === "done" && results && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-slate-800">
                {results.imported.length > 0
                  ? `Imported ${results.imported.length} page${
                      results.imported.length === 1 ? "" : "s"
                    }${results.stopped ? " (stopped early)" : ""}`
                  : "Nothing was imported"}
              </div>
              {results.imported.length > 0 && (
                <ul className="text-xs text-slate-600 space-y-1">
                  {results.imported.map((r) => (
                    <li key={r.url} className="flex items-center gap-1.5">
                      <CheckIcon
                        style={{ fontSize: 14 }}
                        className="text-emerald-600 flex-shrink-0"
                      />
                      <span className="truncate">
                        {formatSourceUrl(r.url) || r.url}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {results.failed.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-rose-800 mb-1">
                    {results.failed.length} page
                    {results.failed.length === 1 ? "" : "s"} couldn't be
                    imported
                  </div>
                  <ul className="text-xs text-rose-700 space-y-1">
                    {results.failed.map((f) => (
                      <li key={f.url} className="truncate">
                        {f.label || f.url} — {f.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200">
          <div className="text-xs text-slate-600 inline-flex items-center gap-1.5">
            <LinkIcon style={{ fontSize: 14 }} className="text-slate-400" />
            {step === "pick"
              ? `${selected.size} of ${allRows.length} selected`
              : useAiSourcesFolder
              ? 'Importing into "Imported pages"'
              : folderName
              ? `Importing into "${folderName}"`
              : ""}
          </div>
          <div className="flex items-center gap-2">
            {step === "url" && (
              <button
                onClick={handleDiscover}
                disabled={!url.trim() || discovering}
                className="px-4 py-2 text-sm font-medium text-white bg-violet-700 rounded-md hover:bg-violet-800 disabled:bg-violet-300 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {discovering && <CircularProgress size={14} color="inherit" />}
                {discovering ? "Reading page…" : "Find pages"}
              </button>
            )}
            {step === "pick" && (
              <>
                <button
                  onClick={() => setStep("url")}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={selected.size === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-violet-700 rounded-md hover:bg-violet-800 disabled:bg-violet-300 disabled:cursor-not-allowed"
                >
                  Import {selected.size || ""} page
                  {selected.size === 1 ? "" : "s"}
                </button>
              </>
            )}
            {step === "importing" && (
              <button
                onClick={() => {
                  stopRef.current = true;
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
              >
                Stop after this page
              </button>
            )}
            {step === "done" && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-violet-700 rounded-md hover:bg-violet-800"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
}
